import type {
  HeartbeatLaneResult,
  HeartbeatModelResult,
  HeartbeatSemanticRejection,
  HeartbeatTarget,
} from "./airshowHeartbeatContract";

export type AirshowRepresentation =
  | "exact-attested"
  | "exact-unattested"
  | "adapted"
  | "mixed"
  | "triangle"
  | "unresolved";

export type AirshowWorkPackage =
  | "seam-witness"
  | "topology-proof"
  | "discrete-geometry"
  | "runtime-extraction"
  | "triangle-evidence"
  | "export-attestation"
  | "component-role"
  | "source-document"
  | "exact-ready";

export interface AirshowRepresentationContract {
  id: AirshowRepresentation;
  label: string;
  eyebrow: string;
  meaning: string;
  allowed: readonly string[];
  blocked: readonly string[];
}

export interface AirshowWorkContract {
  id: AirshowWorkPackage;
  label: string;
  meaning: string;
}

export interface AirshowRepresentationRow {
  ordinal: number;
  entryId: string;
  archiveMemberPath: string;
  sourceSha256: string | null;
  label: string;
  representation: AirshowRepresentation;
  workPackage: AirshowWorkPackage;
  renderable: boolean;
  exactAttested: boolean;
  semanticComplete: boolean;
  exactCode: string;
  exactMessage: string;
  displayCode: string;
  displayMessage: string;
  semanticCode: string;
  displayRepresentation: string | null;
  displayFidelity: string | null;
  sourceComponentCount: number | null;
  realizedComponentCount: number | null;
  sourceSurfaceCount: number | null;
  realizedSurfaceCount: number | null;
  rejectedFeatureIds: readonly string[];
  unsupportedDeclarations: readonly string[];
  semanticRejections: readonly HeartbeatSemanticRejection[];
  allowedOperations: readonly string[];
  blockedOperations: readonly string[];
}

export interface AirshowRepresentationSummary {
  total: number;
  viewable: number;
  exactAttested: number;
  exactUnattested: number;
  adapted: number;
  mixed: number;
  triangle: number;
  unresolved: number;
  renderableNonExact: number;
  semanticComplete: number;
  exactAndSemantic: number;
}

export interface AirshowWorkSummary extends AirshowWorkContract { count: number }

export const AIRSHOW_REPRESENTATION_CONTRACTS: readonly AirshowRepresentationContract[] = [
  {
    id: "exact-attested",
    label: "Exact-attested Bézier",
    eyebrow: "Authoritative surface evidence",
    meaning: "Loom has complete Bézier controls plus accepted source, export-order, and instance evidence.",
    allowed: ["Display and tessellation", "Patch evaluation and derivatives", "Exact-control surface workflows"],
    blocked: ["Semantic source-parameter edits without semantic evidence", "Whole-solid or source round-trip claims without separate receipts"],
  },
  {
    id: "exact-unattested",
    label: "Bézier · proof incomplete",
    eyebrow: "Control net exists",
    meaning: "The captured Bézier net can render and evaluate, but a closure, topology, or export proof is missing.",
    allowed: ["Display and picking", "Candidate control-net evaluation", "Rigid placement after frame binding"],
    blocked: ["Exact trim, Boolean, and periodic-surface claims", "Source-exact or export-exact claims"],
  },
  {
    id: "adapted",
    label: "Adapted sampled surface",
    eyebrow: "Useful non-authoritative geometry",
    meaning: "Loom sampled a complete display from OpenVSP after the exact-control route failed.",
    allowed: ["Display and picking", "Approximate measurement", "Analysis preview with an adapted badge"],
    blocked: ["Exact curvature, intersections, trim, Boolean, or export", "Claims of source-control parity"],
  },
  {
    id: "mixed",
    label: "Mixed Bézier + triangle",
    eyebrow: "Per-body capabilities",
    meaning: "Parametric outer-mold-line surfaces and genuine discrete Human or Mesh bodies coexist in the aircraft.",
    allowed: ["Complete assembly display", "Picking and transforms per body", "Representation-specific operations per part"],
    blocked: ["Promoting the whole assembly to exact", "Cross-representation exact Booleans"],
  },
  {
    id: "triangle",
    label: "Native triangle",
    eyebrow: "Source-native discrete body",
    meaning: "The source geometry is genuinely triangular; inventing Bézier controls would reduce fidelity rather than improve it.",
    allowed: ["Native mesh display and picking", "Mesh-domain measurement", "Rigid placement after frame binding"],
    blocked: ["Bézier control-point editing", "CAD exactness, derivatives, or exact-surface claims"],
  },
  {
    id: "unresolved",
    label: "Unresolved",
    eyebrow: "No complete display body yet",
    meaning: "Loom has source identity and diagnostics, but no complete validated representation safe to insert as an aircraft.",
    allowed: ["Evidence inspection", "Bounded retry and repair actions"],
    blocked: ["Complete scene insertion", "Measurement, analysis, or export"],
  },
] as const;

export const AIRSHOW_WORK_CONTRACTS: readonly AirshowWorkContract[] = [
  { id: "seam-witness", label: "Prove source-exact seams", meaning: "Add a source-bound periodic/seam witness. Never weld or widen tolerances and call the result exact." },
  { id: "topology-proof", label: "Resolve topology proof", meaning: "Disambiguate closure orientation or safely bound the exact topology workload." },
  { id: "discrete-geometry", label: "Keep discrete geometry native", meaning: "Make triangle and point bodies first-class rather than forcing them into Bézier surfaces." },
  { id: "runtime-extraction", label: "Harden bounded extraction", meaning: "Type and fix helper limits, native failures, timeouts, and incomplete realizations without fallback claims." },
  { id: "triangle-evidence", label: "Repair triangle evidence", meaning: "Canonicalize and re-attest native triangle artifacts and result identities." },
  { id: "export-attestation", label: "Complete exact provenance", meaning: "Bind controls, order, instances, helper build, and source into a verifiable exact receipt." },
  { id: "component-role", label: "Classify component roles", meaning: "Resolve the remaining surface, locator, transform, and discrete ownership cases." },
  { id: "source-document", label: "Repair the source document", meaning: "Keep invalid source typed, or provide an explicit non-VSP import path." },
  { id: "exact-ready", label: "Exact evidence ready", meaning: "No exact-lane remediation is recorded for this aircraft." },
] as const;

const REPRESENTATION_BY_ID = new Map(AIRSHOW_REPRESENTATION_CONTRACTS.map((item) => [item.id, item]));
const WORK_BY_ID = new Map(AIRSHOW_WORK_CONTRACTS.map((item) => [item.id, item]));

const WORK_CODES: Readonly<Record<Exclude<AirshowWorkPackage, "exact-ready">, readonly string[]>> = {
  "seam-witness": ["native-closure-unproven"],
  "topology-proof": ["surface-topology-budget-exceeded", "ambiguous-surface-topology-alias"],
  "discrete-geometry": ["native-triangle-exact-inapplicable"],
  "runtime-extraction": [
    "exact-control-helper-contract-failed", "exact-control-native-extraction-failed", "process-timeout",
    "openvsp-worker-signal", "exact-control-helper-process-failed", "openvsp-exact-lane-failed",
    "exact-helper-build-artifact-invalid",
  ],
  "triangle-evidence": ["malformed-native-triangle-artifact", "malformed-native-triangle-results"],
  "export-attestation": ["exact-export-unattested"],
  "component-role": ["openvsp-worker-unknown-component-role"],
  "source-document": ["invalid-source-document"],
};

function lane(model: HeartbeatModelResult, name: HeartbeatLaneResult["lane"]): HeartbeatLaneResult {
  return model.lanes.find((item) => item.lane === name) ?? {
    lane: name,
    status: "unavailable",
    code: `${name}-missing`,
    message: `${name} evidence is missing.`,
    duration_ms: 0,
    metrics: {
      component_count: null, supported_component_count: null, skipped_component_count: null,
      source_component_count: null, realized_component_count: null,
      source_surface_count: null, realized_surface_count: null, skipped_surface_count: null,
      unknown_component_types: [], unknown_xsec_types: [], rejected_feature_ids: [],
      unsupported_declarations: [], semantic_rejections: [], representation: null, fidelity: null,
      geometry_complete: null, export_order_attested: null, artifact_sha256: null,
    },
  };
}

function passed(model: HeartbeatModelResult, target: HeartbeatTarget): boolean {
  return model.targets.find((item) => item.target === target)?.status === "passed";
}

function representationFor(
  model: HeartbeatModelResult,
  exact: HeartbeatLaneResult,
  display: HeartbeatLaneResult,
): AirshowRepresentation {
  if (display.status !== "passed") return "unresolved";
  if (display.metrics.representation === "native-triangle") return "triangle";
  if (display.metrics.representation === "mixed-surface-native-triangle") return "mixed";
  if (passed(model, "exact-attested") && exact.status === "passed"
      && exact.metrics.representation === "exact-bezier"
      && exact.metrics.fidelity === "exact-attested"
      && exact.metrics.export_order_attested === true) return "exact-attested";
  if (display.metrics.representation === "exact-bezier") return "exact-unattested";
  if (display.metrics.representation === "sampled-grid"
      && display.metrics.fidelity === "adapted") return "adapted";
  return "unresolved";
}

function workFor(code: string): AirshowWorkPackage {
  for (const [id, codes] of Object.entries(WORK_CODES) as Array<[Exclude<AirshowWorkPackage, "exact-ready">, readonly string[]]>) {
    if (codes.includes(code)) return id;
  }
  return code === "ok" ? "exact-ready" : "runtime-extraction";
}

export function buildAirshowRepresentationRows(
  models: readonly HeartbeatModelResult[],
  labelById: ReadonlyMap<string, string> = new Map(),
): readonly AirshowRepresentationRow[] {
  return models.map((model) => {
    const exact = lane(model, "exact");
    const display = lane(model, "display");
    const semantic = lane(model, "semantic");
    const representation = representationFor(model, exact, display);
    const contract = REPRESENTATION_BY_ID.get(representation)!;
    return {
      ordinal: model.ordinal,
      entryId: model.entry_id,
      archiveMemberPath: model.archive_member_path,
      sourceSha256: model.source_sha256,
      label: labelById.get(model.entry_id) ?? model.entry_id.split("/").pop() ?? model.entry_id,
      representation,
      workPackage: workFor(exact.code),
      renderable: passed(model, "renderable") && representation !== "unresolved",
      exactAttested: passed(model, "exact-attested"),
      semanticComplete: passed(model, "semantic-complete"),
      exactCode: exact.code,
      exactMessage: exact.message,
      displayCode: display.code,
      displayMessage: display.message,
      semanticCode: semantic.code,
      displayRepresentation: display.metrics.representation,
      displayFidelity: display.metrics.fidelity,
      sourceComponentCount: display.metrics.source_component_count ?? exact.metrics.source_component_count,
      realizedComponentCount: display.metrics.realized_component_count ?? exact.metrics.realized_component_count,
      sourceSurfaceCount: display.metrics.source_surface_count ?? exact.metrics.source_surface_count,
      realizedSurfaceCount: display.metrics.realized_surface_count ?? exact.metrics.realized_surface_count,
      rejectedFeatureIds: semantic.metrics.rejected_feature_ids,
      unsupportedDeclarations: semantic.metrics.unsupported_declarations,
      semanticRejections: semantic.metrics.semantic_rejections,
      allowedOperations: contract.allowed,
      blockedOperations: contract.blocked,
    };
  });
}

export function summarizeAirshowRepresentations(
  rows: readonly AirshowRepresentationRow[],
): AirshowRepresentationSummary {
  const count = (id: AirshowRepresentation) => rows.filter((row) => row.representation === id).length;
  const capabilities = rows.map((row) => {
    const viewable = row.renderable && row.representation !== "unresolved";
    const exact = viewable && row.representation === "exact-attested";
    const semantic = viewable && row.semanticComplete;
    return { viewable, exact, semantic, both: exact && semantic };
  });
  return {
    total: rows.length,
    viewable: capabilities.filter(({ viewable }) => viewable).length,
    exactAttested: capabilities.filter(({ exact }) => exact).length,
    exactUnattested: count("exact-unattested"),
    adapted: count("adapted"),
    mixed: count("mixed"),
    triangle: count("triangle"),
    unresolved: count("unresolved"),
    renderableNonExact: capabilities.filter(({ viewable, exact }) => viewable && !exact).length,
    semanticComplete: capabilities.filter(({ semantic }) => semantic).length,
    exactAndSemantic: capabilities.filter(({ both }) => both).length,
  };
}

export function summarizeAirshowWork(rows: readonly AirshowRepresentationRow[]): readonly AirshowWorkSummary[] {
  return AIRSHOW_WORK_CONTRACTS.filter(({ id }) => id !== "exact-ready").map((contract) => ({
    ...contract,
    count: rows.filter((row) => row.workPackage === contract.id).length,
  })).filter(({ count }) => count > 0);
}

export function airshowWorkContract(id: AirshowWorkPackage): AirshowWorkContract {
  return WORK_BY_ID.get(id)!;
}
